// Minimal, dependency-free focus helper for modal overlays that use a DS
// component (e.g. Panel) which does not itself provide dialog semantics.
//
// Context: the DS `Panel` (molecules/Panel) renders `role="region"` and is
// documented as a non-modal "sidebar/drawer" primitive — it has no focus
// trap. There is no DS `Drawer`, and the DS `Modal` (atoms/Modal) is hard-
// coded to a centred layout (no drawer/placement variant), so it can't be
// swapped in without changing the phone-call drawer's visual placement.
// This hook adds the missing modal behaviour to any `open`-gated container
// without pulling in a new dependency: moving initial focus in on open,
// restoring focus to the trigger on close, and cycling Tab/Shift+Tab among
// the container's OWN (non-portaled) descendants.
//
// IMPORTANT — this hook does NOT by itself guarantee focus containment.
// Its Tab/Shift+Tab cycling only ever sees elements that are literal DOM
// descendants of `containerRef` (it's a `keydown` listener on that node —
// native DOM bubbling, not React's portal-aware bubbling). Content rendered
// via `createPortal` elsewhere in the document (e.g. a popover that portals
// to `document.body`) is invisible to it. A real "nothing outside the
// modal is reachable" guarantee — including for portaled descendants —
// requires marking everything OUTSIDE the modal `inert` (see
// `dashboard/dashboard-view.tsx`, which is this hook's only current
// consumer and does exactly that); this hook alone is not sufficient
// dialog-focus-containment on a surface with portaled content.
import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function getFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => el.offsetParent !== null, // visible (skips display:none subtrees)
  );
}

/**
 * Traps focus within `containerRef` while `open` is true: moves focus into
 * the container on open, cycles Tab/Shift+Tab within it, and returns focus
 * to whatever was focused before opening (typically the trigger button)
 * once `open` goes back to false.
 */
export function useFocusTrap(containerRef: React.RefObject<HTMLElement | null>, open: boolean): void {
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const container = containerRef.current;
    if (!container) return;

    previouslyFocused.current = document.activeElement as HTMLElement | null;

    const focusable = getFocusable(container);
    (focusable[0] ?? container).focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const current = getFocusable(container);
      if (current.length === 0) {
        e.preventDefault();
        return;
      }
      const first = current[0];
      const last = current[current.length - 1];
      const active = document.activeElement;

      if (e.shiftKey) {
        if (active === first || !container.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last || !container.contains(active)) {
        e.preventDefault();
        first.focus();
      }
    };

    container.addEventListener('keydown', handleKeyDown);
    return () => {
      container.removeEventListener('keydown', handleKeyDown);
      previouslyFocused.current?.focus?.();
    };
  }, [open, containerRef]);
}
